// src/pages/CtcScreeningDetailPage.js
// Route: /ctc-screenings/:id — ADMIN / HEALTH_NAVIGATOR (backend-enforced).
//
// Mirrors PatientDetailPage.js: navy hero + tabs. Tabs are Overview (screening
// results, personal and camp info), Case Status (the seven follow-up flags,
// toggled inline) and Notes.

import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AppShell } from '../components/Layout';
import { ctcScreeningAPI } from '../services/api';
import { useToast } from '../context/ToastContext';
import Icon from '../components/Icon';
import { CASE_FLAGS, statusMeta, formatDate, ageLabel } from '../utils/ctcScreening';

const TABS = ['Overview', 'Case Status', 'Notes'];
const MODALITIES = [...new Set(CASE_FLAGS.map(f => f.modality))];

function Field({ label, value }) {
  return (
    <div className="field-group">
      <div className="field-label">{label}</div>
      <p className="field-text">{value || '—'}</p>
    </div>
  );
}

function ExamField({ label, done, result }) {
  return (
    <div className="field-group">
      <div className="field-label">{label}</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span className={`badge ${done ? 'badge-active' : 'badge-unknown'}`}>{done ? 'Done' : 'Not done'}</span>
        {result && <span className="field-text">{result}</span>}
      </div>
    </div>
  );
}

export default function CtcScreeningDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [record,    setRecord]    = useState(null);
  const [loading,   setLoading]   = useState(true);
  const [activeTab, setActiveTab] = useState('Overview');
  const [togglingKey, setTogglingKey] = useState(null);

  const [notes,       setNotes]       = useState('');
  const [notesSaving, setNotesSaving] = useState(false);
  const [notesSavedJustNow, setNotesSavedJustNow] = useState(false);

  // The backend sends aadharNumberMasked only; the real number is fetched on an
  // explicit Show click (an audited request) and held just in this state.
  // Nothing caches it, so leaving the page re-masks it. currentId stops a
  // reveal that is still in flight from landing on a different record.
  const [revealedAadhar, setRevealedAadhar] = useState(null);
  const [revealingAadhar, setRevealingAadhar] = useState(false);
  const currentId = useRef(id);

  useEffect(() => {
    currentId.current = id;
    setRevealedAadhar(null);
    setRevealingAadhar(false);
    setLoading(true);
    ctcScreeningAPI.get(id)
      .then(r => { setRecord(r.data); setNotes(r.data.notes || ''); })
      .catch(() => navigate('/ctc-screenings'))
      .finally(() => setLoading(false));
  }, [id, navigate]);

  useEffect(() => () => { currentId.current = null; }, []);

  async function toggleAadhar() {
    if (revealedAadhar !== null) { setRevealedAadhar(null); return; }
    const requestedId = id;
    setRevealingAadhar(true);
    try {
      const { data } = await ctcScreeningAPI.getAadharNumber(requestedId);
      if (currentId.current === requestedId) setRevealedAadhar(data.aadharNumber || '');
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Could not load the Aadhar number — please try again');
    } finally {
      if (currentId.current === requestedId) setRevealingAadhar(false);
    }
  }

  // One flag per request — the backend's partial update leaves every other
  // field untouched, so there's no risk of overwriting a concurrent edit.
  async function toggleFlag(key) {
    setTogglingKey(key);
    try {
      const { data } = await ctcScreeningAPI.update(id, { [key]: !record[key] });
      setRecord(data);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Could not update case status — please try again');
    } finally {
      setTogglingKey(null);
    }
  }

  async function saveNotes() {
    setNotesSaving(true);
    try {
      const { data } = await ctcScreeningAPI.update(id, { notes });
      setRecord(data);
      setNotesSavedJustNow(true);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Could not save notes — please try again');
    } finally {
      setNotesSaving(false);
    }
  }

  if (loading) return (
    <AppShell breadcrumbs={[{ label: 'CTC Screenings', to: '/ctc-screenings' }, { label: 'Loading…' }]}>
      <div className="loading-state"><div className="spinner" />Loading screening…</div>
    </AppShell>
  );

  if (!record) return null;

  // lastName is optional, so join only what's there — never "null" or a trailing space.
  const fullName = [record.firstName, record.lastName].filter(Boolean).join(' ');
  const initials = `${record.firstName?.[0] || ''}${record.lastName?.[0] || ''}`.toUpperCase();
  const status   = statusMeta(record.status);
  const isConverted = record.status === 'CONVERTED_TO_PATIENT';
  const activeFlags = CASE_FLAGS.filter(f => record[f.key]);
  const age = ageLabel(record);

  return (
    <AppShell breadcrumbs={[
      { label: 'CTC Screenings', to: '/ctc-screenings' },
      { label: fullName },
    ]}>
      <span className="back-link fade-in-up" onClick={() => navigate('/ctc-screenings')}>
        ← Back to CTC Screenings
      </span>

      {/* Hero */}
      <div className="patient-hero fade-in-up delay-1">
        <div className="patient-avatar-lg" role="img" aria-label={fullName} title={fullName}>{initials}</div>
        <div style={{ flex: 1 }}>
          <div className="patient-name-lg">{fullName}</div>
          <div className="patient-meta-lg">
            {record.gender || 'Gender not recorded'}
            {age && <>&nbsp;·&nbsp;{age}</>}
            {record.district && <>&nbsp;·&nbsp;{record.district}</>}
            {record.campName && <>&nbsp;·&nbsp;{record.campName}</>}
            {record.registeredDate && <>&nbsp;·&nbsp;Screened {formatDate(record.registeredDate)}</>}
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <span className={`badge ${status.badge}`}>{status.label}</span>
            {record.overallResult && (
              <span className="badge badge-unknown badge-prose">Result: {record.overallResult}</span>
            )}
            {activeFlags.length > 0 && (
              <span className="badge badge-moderate">
                {activeFlags.length} case flag{activeFlags.length !== 1 ? 's' : ''} set
              </span>
            )}
          </div>
        </div>
        <div className="patient-hero-actions">
          <button className="btn btn-ghost" onClick={() => navigate(`/ctc-screenings/${id}/edit`)}>
            ✏ Edit
          </button>
          {isConverted ? (
            record.patientId && (
              <button className="btn btn-accent" onClick={() => navigate(`/patients/${record.patientId}`)}>
                View Patient Record →
              </button>
            )
          ) : (
            // The Convert-to-Patient wizard ships as a follow-up PR (guide §5)
            // once these base screens are confirmed against real camp data.
            <button className="btn btn-accent" disabled title="Coming in a follow-up release">
              Convert to CTC Patient
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="tabs-bar fade-in-up delay-2">
        {TABS.map(t => (
          <button key={t} className={`tab-btn ${activeTab === t ? 'active' : ''}`}
            onClick={() => setActiveTab(t)}>
            {t}
            {t === 'Case Status' && activeFlags.length > 0 && (
              <span className="tab-count">{activeFlags.length}</span>
            )}
          </button>
        ))}
      </div>

      {/* ── OVERVIEW ── */}
      {activeTab === 'Overview' && (
        <div className="detail-grid">
          <div className="card fade-in-up">
            <div className="card-title"><span className="card-title-icon"><Icon name="stethoscope" size={15} /></span>Screening Results</div>
            <ExamField label="Oral examination" done={record.oralExaminationDone} result={record.oralExaminationResult} />
            <ExamField label="Breast examination (CBE)" done={record.breastExaminationDone} result={record.breastExaminationResult} />
            <ExamField label="PAP smear" done={record.papSmearDone} result={record.papSmearResult} />
            <Field label="Cervical infections" value={record.cervicalInfections} />
            <Field label="Overall result" value={record.overallResult} />
            <Field label="Camp remarks" value={record.campRemarks} />
          </div>

          <div className="card fade-in-up delay-1">
            <div className="card-title"><span className="card-title-icon"><Icon name="person" size={15} /></span>Personal &amp; Contact</div>
            <Field label="Phone" value={record.phone} />
            <Field label="Email" value={record.email} />
            <Field label="Date of birth" value={formatDate(record.dateOfBirth)} />
            <Field label="Age at screening" value={record.ageAtScreening != null ? `${record.ageAtScreening} yrs` : null} />
            <div className="field-group">
              <div className="field-label">Aadhar number</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span className="field-text">
                  {revealedAadhar !== null ? (revealedAadhar || '—') : (record.aadharNumberMasked || '—')}
                </span>
                {record.aadharNumberMasked && (
                  <button className="btn btn-sm btn-ghost" disabled={revealingAadhar} onClick={toggleAadhar}>
                    {revealingAadhar ? 'Loading…' : revealedAadhar !== null ? 'Hide' : 'Show'}
                  </button>
                )}
              </div>
            </div>
            <Field label="Citizen number" value={record.citizenNumber} />
            <Field label="Marital status" value={record.maritalStatus} />
            <Field label="Guardian" value={record.guardianName} />
            <Field label="Emergency contact" value={record.emergencyContact} />
            <Field label="Address" value={record.address} />
          </div>

          <div className="card fade-in-up delay-2">
            <div className="card-title"><span className="card-title-icon"><Icon name="pin" size={15} /></span>Camp &amp; Organizer</div>
            <Field label="Camp" value={[record.campName, record.campLocation].filter(Boolean).join(' — ')} />
            <Field label="District" value={record.district} />
            <Field label="Registered date" value={formatDate(record.registeredDate)} />
            <Field label="Referral source" value={record.referralSource} />
            <Field label="Campaign coordinator" value={record.campaignCoordinator} />
            <Field label="Organizer" value={[record.organizerName, record.organizerRelationship].filter(Boolean).join(' · ')} />
            <Field label="Organizer contact" value={[record.organizerPhone, record.organizerEmail].filter(Boolean).join(' · ')} />
            <Field label="Markers" value={record.markers} />
            <Field label="Navigator" value={record.navigatorName} />
          </div>
        </div>
      )}

      {/* ── CASE STATUS ── */}
      {activeTab === 'Case Status' && (
        <div className="detail-grid fade-in-up">
          {MODALITIES.map(m => (
            <div className="card" key={m}>
              <div className="card-title">{m}</div>
              <div className="card-list">
                {CASE_FLAGS.filter(f => f.modality === m).map(f => {
                  const on = Boolean(record[f.key]);
                  const busy = togglingKey === f.key;
                  return (
                    <div className="card-row" key={f.key}>
                      <div className="card-row-body">
                        <div className="card-row-title">{f.label}</div>
                        <div className="card-row-meta">
                          <span className={`badge ${on ? f.badge : 'badge-unknown'}`}>{on ? 'Yes' : 'No'}</span>
                        </div>
                      </div>
                      <button
                        className={`btn btn-sm ${on ? 'btn-ghost' : 'btn-secondary'}`}
                        disabled={togglingKey !== null}
                        onClick={() => toggleFlag(f.key)}
                      >
                        {busy ? 'Saving…' : on ? 'Clear' : 'Mark'}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── NOTES ── */}
      {activeTab === 'Notes' && (
        <div className="card fade-in-up">
          <div className="card-title"><span className="card-title-icon"><Icon name="note" size={15} /></span>Navigator Notes</div>
          <textarea
            className="form-textarea"
            style={{ width: '100%', minHeight: 200 }}
            placeholder="Follow-up calls, referrals, anything the next navigator should know…"
            value={notes}
            onChange={e => { setNotes(e.target.value); setNotesSavedJustNow(false); }}
          />
          <div className="form-actions">
            <button className="btn btn-primary" disabled={notesSaving} onClick={saveNotes}>
              {notesSaving ? 'Saving…' : 'Save Notes'}
            </button>
            {notesSavedJustNow && !notesSaving && (
              <span className="form-hint">✓ Saved</span>
            )}
          </div>
        </div>
      )}
    </AppShell>
  );
}
